import { WidgetProps } from '@rjsf/utils'
import React, { useMemo } from 'react'
import { Dropdown, ListGroup } from 'react-bootstrap'
import { PromptSegment } from '../../store/ai/promptSegment'
import { Button } from '../Button/Button'
import styles from './AgendaSmartSuggestionsWidget.module.scss'

interface AgendaSmartSuggestionsWidgetOptions {
  smartSuggestionOptions?: PromptSegment[]
}

export const AgendaSmartSuggestionsWidget: React.FC<WidgetProps> = (props) => {
  const { options, onChange, value, disabled, readonly } = props

  const smartSuggestionOptions = useMemo(() => {
    return (options as AgendaSmartSuggestionsWidgetOptions)?.smartSuggestionOptions ?? []
  }, [options])

  const selectedValues = useMemo(() => {
    if (Array.isArray(value)) {
      return value.map(String)
    }
    return []
  }, [value])

  const availableOptions = useMemo(() => {
    return smartSuggestionOptions.filter((segment) => !selectedValues.includes(segment.id))
  }, [smartSuggestionOptions, selectedValues])

  const handleSelect = (selectedValue: string | null) => {
    if (selectedValue && !selectedValues.includes(selectedValue)) {
      onChange([...selectedValues, selectedValue])
    }
  }

  const handleRemove = (valueToRemove: string) => {
    onChange(selectedValues.filter((v) => v !== valueToRemove))
  }

  const getSegment = (id: string) => {
    return smartSuggestionOptions.find((segment) => segment.id === id)
  }

  const isDisabled = disabled || readonly
  const allSegmentsSelected = availableOptions.length === 0

  return (
    <div>
      <Dropdown onSelect={handleSelect} className='mb-3'>
        <Dropdown.Toggle
          as='button'
          id='smart-suggestions-dropdown'
          disabled={isDisabled || allSegmentsSelected}
          className={`btn w-100 d-flex justify-content-between align-items-center text-start bg-white zocks-border-gray-500 paragraph-medium py-2 zocks-px-2.25 ${styles.dropdownToggle}`}
        >
          <span>{allSegmentsSelected ? 'All segments selected' : 'Pick a topic to add to the template'}</span>
          <i className='bi bi-chevron-expand ms-2'></i>
        </Dropdown.Toggle>

        <Dropdown.Menu className='w-100 border zocks-border-0.5 py-1'>
          {availableOptions.map((segment) => (
            <Dropdown.Item key={segment.id} eventKey={segment.id} className='py-2 px-3 text-wrap'>
              <p className='paragraph-medium mb-0 zocks-text-gray-900'>{segment.title}</p>
              {segment.description ? <p className='paragraph-small mb-0 zocks-text-gray-600'>{segment.description}</p> : null}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {selectedValues.length > 0 ? (
        <ListGroup className='border-0'>
          {selectedValues.map((selectedValue, index) => {
            const segment = getSegment(selectedValue)
            const isFirst = index === 0
            return (
              <ListGroup.Item
                key={selectedValue}
                className={`d-flex justify-content-between align-items-center paragraph-medium px-0 rounded-0 border-start-0 border-end-0 ${isFirst ? '' : 'border-top-0'}`}
              >
                <span>{segment?.title || selectedValue}</span>
                {!isDisabled ? (
                  <Button className='btn btn-link' aria-label='Remove' onClick={() => handleRemove(selectedValue)}>
                    <i className='bi bi-trash fs-6' />
                  </Button>
                ) : null}
              </ListGroup.Item>
            )
          })}
        </ListGroup>
      ) : null}
    </div>
  )
}
